import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, LayoutDashboard, FileText, Users, Settings, BarChart } from 'lucide-react';
import logo from './assets/logo.png';

export const AdminHeader = () => {
  const navigate = useNavigate();
  const [openDropdown, setOpenDropdown] = useState(null);

  const handleLogout = () => {
    // Clear authentication
    sessionStorage.removeItem('isAuthenticated');
    sessionStorage.removeItem('userEmail');
    navigate('/');
  };

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      path: '/admin/dashboard'
    },
    {
      id: 'forms',
      label: 'Forms',
      icon: FileText,
      subItems: [
        { label: 'Entry Form', path: '/admin/entry-form' },
        { label: 'Customer Form', path: '/admin/customer-form' },
        { label: 'Project Form', path: '/admin/project-form' },
        { label: 'Invoice Form', path: '/admin/invoice-form' }
      ]
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: BarChart,
      subItems: [
        { label: 'Sales Report', path: '/admin/sales-report' },
        { label: 'Customer Report', path: '/admin/customer-report' },
        { label: 'Project Report', path: '/admin/project-report' }
      ]
    },
    {
      id: 'management',
      label: 'Management',
      icon: Users,
      subItems: [
        { label: 'Users', path: '/admin/users' },
        { label: 'Roles', path: '/admin/roles' },
        { label: 'Permissions', path: '/admin/permissions' }
      ]
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      path: '/admin/settings'
    }
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[rgba(10,37,64,0.98)] border-b-2 border-[#00a8ff] backdrop-blur-md" style={{ boxShadow: '0 0 30px rgba(0,168,255,0.5)' }}>
      <div className="container mx-auto px-6 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/admin/dashboard" className="flex items-center space-x-3 group">
            <img
              src={logo}
              alt="Max Bien Logo" width={160} height={48}
              className="h-12 transition-all duration-300 group-hover:scale-110"
              style={{ filter: 'brightness(0) saturate(100%) invert(61%) sepia(98%) saturate(3180%) hue-rotate(175deg) brightness(103%) contrast(101%) drop-shadow(0 0 15px #00a8ff)' }}
            />
            <span className="text-2xl font-bold led-text tracking-wider animate-led-pulse hidden sm:block" style={{ textShadow: '0 0 15px #00a8ff' }}>
              MAX BIEN
            </span>
          </Link>

          {/* Horizontal Navigation Menu */}
          <nav className="hidden lg:flex items-center space-x-1">
            {menuItems.map((item) => {
              const IconComponent = item.icon;
              
              if (item.subItems) {
                return (
                  <div
                    key={item.id}
                    className="relative group"
                    onMouseEnter={() => setOpenDropdown(item.id)}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    <button className="flex items-center space-x-2 px-4 py-2 rounded-md font-bold transition-all duration-300 hover:bg-[rgba(0,168,255,0.2)] led-text hover:shadow-[0_0_20px_rgba(0,168,255,0.8)]" style={{ textShadow: '0 0 10px #00a8ff' }}>
                      <IconComponent size={18} />
                      <span>{item.label}</span>
                      <ChevronDown size={16} className={`transition-transform duration-300 ${openDropdown === item.id ? 'rotate-180' : ''}`} />
                    </button>
                    {/* Invisible bridge */}
                    <div className="absolute top-full left-0 w-56 h-2 opacity-0" style={{ pointerEvents: 'auto' }}></div>
                    <div className={`absolute top-full left-0 pt-2 w-56 transition-all duration-300 z-50 ${openDropdown === item.id ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
                      <div className="bg-[rgba(10,37,64,0.98)] border-2 border-[#00a8ff] rounded-xl shadow-[0_0_30px_#00a8ff] backdrop-blur-md overflow-hidden">
                        {item.subItems.map((subItem, idx) => (
                          <Link
                            key={idx}
                            to={subItem.path}
                            className="flex items-center px-6 py-3 led-text hover:bg-[rgba(0,168,255,0.3)] hover:pl-8 transition-all duration-300 border-b border-[rgba(0,168,255,0.2)] last:border-b-0"
                            style={{ textShadow: '0 0 10px #00a8ff' }}
                          >
                            <span className="text-[#00a8ff] mr-3 text-xl">→</span>
                            <span>{subItem.label}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className="flex items-center space-x-2 px-4 py-2 rounded-md font-bold transition-all duration-300 hover:bg-[rgba(0,168,255,0.2)] led-text hover:shadow-[0_0_20px_rgba(0,168,255,0.8)]"
                  style={{ textShadow: '0 0 10px #00a8ff' }}
                >
                  <IconComponent size={18} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Right Section */}
          <div className="flex items-center space-x-4">
            <Link to="/">
              <button className="bg-[rgba(0,168,255,0.2)] border border-[#00a8ff] text-[#00a8ff] hover:bg-[#00a8ff] hover:text-[#0a2540] font-bold px-4 py-2 rounded-lg transition-all duration-300 text-sm" style={{ boxShadow: '0 0 15px rgba(0,168,255,0.3)' }}>
                Back to Website
              </button>
            </Link>
            <button
              onClick={handleLogout}
              className="bg-[rgba(255,0,0,0.2)] border border-red-500 text-red-500 hover:bg-red-500 hover:text-white font-bold px-4 py-2 rounded-lg transition-all duration-300 flex items-center space-x-2 text-sm"
              style={{ boxShadow: '0 0 15px rgba(255,0,0,0.3)' }}
            >
              <LogOut size={16} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};
